import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { getApi } from "../../../Services/apiService";
import { getFamilyTreeIdFromStorage } from "../../../Utils/headOfLineRole";
import { canViewPhoneNumberReport } from "../../../Utils/phoneNumberReportAccess";
import { isLoggedInUserTreeAdminFromStorage } from "../../../Utils/subscriptionAccess";
import { buildMemberRecordEditPath } from "../../../Utils/memberRecordEditLink";
import { getMemberAgeLabel, getMemberDobTimestamp } from "../../../Utils/family-tree/dateAndTime";
import {
  formatDashboardMemberDisplayName,
  getDashboardMemberNameParts,
  isDashboardInLawMember,
} from "../../../Utils/dashboardMemberDisplayName";
import { isExplicitDashboardStartOrTopOfLineMember } from "../../../Utils/dashboardStartOfLineMember";
import {
  buildMultipleBirthSetSizes,
  getMultipleBirthLegendTags,
  getMultipleBirthSuperscriptText,
  getMultipleBirthTagForSize,
} from "../../../Utils/dashboardMultipleBirthSuperscript";
import {
  buildDashboardColorLegendLines,
  buildHeadsOfLineIdByColorMap,
  extractAllMembersFromStatisticsResponse,
  extractHeadsOfLineFromStatisticsResponse,
  getDashboardFamilyLineHeadDisplayName,
} from "../../../Utils/dashboardFamilyLineNavigation";
import DashboardBackButton from "../DashboardComponents/DashboardBackButton";
import DashboardDetailSearchBox, {
  DashboardDetailSearchNoMatch,
} from "../DashboardComponents/DashboardDetailSearchBox";
import DashboardLineColorName from "../DashboardComponents/DashboardLineColorName";
import DashboardMarkerLegend from "../DashboardComponents/DashboardMarkerLegend";
import DashboardColorLegend from "../DashboardComponents/DashboardColorLegend";
import { DashboardInLawAsterisk } from "../DashboardComponents/DashboardPhotoViewer";
import "./PhoneNumberReport.css";

const toText = (value) => (value === null || value === undefined ? "" : String(value).trim());

const formatPhone = (code, phone) => {
  const number = toText(phone);
  if (!number) return "";
  const cc = toText(code);
  return cc ? `${cc.startsWith("+") ? cc : `+${cc}`} ${number}` : number;
};

const isUsCode = (code) => !toText(code) || toText(code).replace(/^\+/, "") === "1";

// A U.S. number reads 301-310-5694; anything else is shown as entered.
const hyphenateUsNumber = (number) => {
  const digits = toText(number).replace(/\D/g, "");
  const national = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  return national.length === 10
    ? `${national.slice(0, 3)}-${national.slice(3, 6)}-${national.slice(6)}`
    : toText(number);
};

// Cell phone and email come from the person's linked user account — the only
// contact fields the contact report returns (no work phone).
const getCellPhone = (m) => formatPhone(m?.user_country_code, m?.user_phone);
// The list drops the +1 on U.S. numbers and hyphenates them; the tel: link
// keeps the +1.
const getListPhone = (m) =>
  isUsCode(m?.user_country_code) ? hyphenateUsNumber(m?.user_phone) : getCellPhone(m);
const getEmail = (m) => toText(m?.user_email);

// An address with "delete" in it was retired and needs replacing; it is not a
// current email.
const isRetiredEmail = (email) => /delete/i.test(email || "");

// The contact report carries only the linked user account's email and phone, so
// an email or cell number entered on the member's own record (the Family Tree
// form) is read from the tree hierarchy and used when the account has none.
const collectRecordContacts = (tree) => {
  const byId = new Map();
  const walk = (nodes) => {
    (Array.isArray(nodes) ? nodes : []).forEach((node) => {
      if (node?.id) byId.set(node.id, node);
      walk(node?.children);
    });
  };
  walk(tree);
  return byId;
};

const pickContact = (email, phone, code, record) => {
  const recordPhone = toText(record.phone);
  const hasPhone = !!toText(phone);
  return {
    email: toText(email) || toText(record.email),
    phone: hasPhone ? phone : recordPhone,
    code: hasPhone ? code : recordPhone ? record.code : code,
  };
};

// The report lists an in-law as a row of their own (is_spouse, id
// "<member id>_spouse"); their record contact is the spouse side of that
// member's record.
const SPOUSE_ID_SUFFIX = "_spouse";
const isTruthyFlag = (value) => value === true || value === "true" || value === 1;
const isSpouseRow = (contact) => isTruthyFlag(contact?.is_spouse);

const withSpouseRowRecordContact = (contact, record) => {
  if (!record) return contact;
  const spouse = pickContact(contact.user_email, contact.user_phone, contact.user_country_code, {
    email: record.spouse_email || record.spouse_email_from_user,
    phone: record.spouse_phone || record.spouse_phone_number_from_user,
    code: record.spouse_country_code,
  });
  return {
    ...contact,
    user_email: spouse.email,
    user_phone: spouse.phone,
    user_country_code: spouse.code,
  };
};

const withRecordContact = (contact, record) => {
  if (!record) return contact;
  if (isSpouseRow(contact)) return withSpouseRowRecordContact(contact, record);
  const own = pickContact(contact.user_email, contact.user_phone, contact.user_country_code, {
    email: record.email || record.primary_email,
    phone: record.phone || record.primary_phone_number,
    code: record.country_code,
  });
  const spouse = pickContact(
    contact.spouse_user_email,
    contact.spouse_user_phone,
    contact.spouse_user_country_code,
    {
      email: record.spouse_email || record.spouse_email_from_user,
      phone: record.spouse_phone || record.spouse_phone_number_from_user,
      code: record.spouse_country_code,
    }
  );
  return {
    ...contact,
    user_email: own.email,
    user_phone: own.phone,
    user_country_code: own.code,
    spouse_user_email: spouse.email,
    spouse_user_phone: spouse.phone,
    spouse_user_country_code: spouse.code,
  };
};

// The present/deceased status decides; a date of death counts only when the
// status is blank, as some living members carry a stale date of death.
const isDeceased = (m) => {
  const status = toText(m?.present_or_deceased);
  return status ? Number(status) === 2 : !!toText(m?.date_of_death);
};

/** Age today, from the date of birth; the member's stored `age` only when there is no date of birth. */
const getCurrentAge = (m) => {
  const dob = getMemberDobTimestamp(m);
  if (dob === -1) {
    const given = Number(m?.age);
    return m?.age !== null && m?.age !== undefined && m?.age !== "" && Number.isFinite(given)
      ? given
      : null;
  }
  const birth = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birth.getUTCFullYear();
  const beforeBirthday =
    today.getMonth() < birth.getUTCMonth() ||
    (today.getMonth() === birth.getUTCMonth() && today.getDate() < birth.getUTCDate());
  if (beforeBirthday) age -= 1;
  return age >= 0 ? age : null;
};

const nameKey = (m) =>
  [m?.first_name, m?.last_name].map((part) => toText(part).toLowerCase()).join("|");

/** The in-law on a member's row (spouse_* fields), as a row of their own on that member's line. */
const getSpouseContact = (member) => {
  const first = toText(member?.spouse_first_name);
  const last = toText(member?.spouse_last_name);
  if (!first && !last) return null;
  return {
    id: `${member.id}-spouse`,
    first_name: first,
    middle_name: toText(member?.spouse_middle_name),
    last_name: last,
    suffix: toText(member?.spouse_suffix),
    present_or_deceased: member?.spouse_present_or_deceased,
    date_of_death: member?.spouse_date_of_death,
    user_email: member?.spouse_user_email,
    user_phone: member?.spouse_user_phone,
    user_country_code: member?.spouse_user_country_code,
    color_name: member?.color_name,
    hex_code: member?.hex_code,
    generation_level: member?.generation_level,
    is_in_laws: true,
  };
};

const getName = (m) =>
  formatDashboardMemberDisplayName(m) ||
  [m?.first_name, m?.last_name].map(toText).filter(Boolean).join(" ") ||
  "Unnamed Member";

const SORTS = [
  { value: "alpha-asc", label: "A - Z" },
  { value: "alpha-desc", label: "Z - A" },
  { value: "color", label: "Line Color" },
];

// Age sorts oldest first; clicking it again flips to youngest first.
const AGE_SORTS = { "age-desc": "Age: Oldest", "age-asc": "Age: Youngest" };

// Every view leaves out the deceased. A "delete" email does not count as an email.
//  - all: every living member and in-law, with or without contact details.
//  - with-phone: a cell phone, with or without an email.
//  - with-email: a current email, with or without a cell phone.
//  - missing: no cell phone, no current email, or neither.
//  - missing-both: no cell phone and no current email.
//  - deleted-email: a retired ("delete") email, which the list then shows.
const VIEW_FILTERS = [
  { value: "with-phone", label: "With Cell Phone" },
  { value: "with-email", label: "With Email" },
  { value: "missing", label: "Missing Cell/Email" },
  { value: "missing-both", label: "Missing Both" },
  { value: "deleted-email", label: "Deleted Email" },
];

// The in-laws who carry the asterisk on the list; a start/top-of-line in-law is
// listed as the head of their line and is left out.
const isMarkedInLaw = (m) => isDashboardInLawMember(m) && !isExplicitDashboardStartOrTopOfLineMember(m);

const isInView = (m, view) => {
  if (view === "missing") return !m._phone || !m._email;
  if (view === "missing-both") return !m._phone && !m._email;
  if (view === "with-phone") return !!m._phone;
  if (view === "with-email") return !!m._email;
  if (view === "deleted-email") return !!m._retiredEmail;
  return true;
};

// The filters survive a trip to a member's record and back (for the session).
const FILTERS_STORAGE_KEY = "contactListFilters";
const readSavedFilters = () => {
  try {
    return JSON.parse(sessionStorage.getItem(FILTERS_STORAGE_KEY) || "{}") || {};
  } catch {
    return {};
  }
};

function PhoneNumberReport() {
  const allowed = canViewPhoneNumberReport();
  const navigate = useNavigate();
  const location = useLocation();
  // Admins click a name to open that member's record, fill in what is missing
  // and come back here once it is saved.
  const canEditRecords = isLoggedInUserTreeAdminFromStorage();
  const [members, setMembers] = useState([]);
  const [treeMembers, setTreeMembers] = useState([]);
  // The statistics multiple_births section: one row per member of a set, tagged
  // with the set's badge even where the member records carry no link.
  const [multipleBirthRows, setMultipleBirthRows] = useState([]);
  const [recordContacts, setRecordContacts] = useState(() => new Map());
  const [isCompactLayout, setIsCompactLayout] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= 768
  );
  const [headsMap, setHeadsMap] = useState(() => buildHeadsOfLineIdByColorMap([]));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [savedFilters] = useState(readSavedFilters);
  const [searchQuery, setSearchQuery] = useState(savedFilters.searchQuery ?? "");
  const [sortBy, setSortBy] = useState(savedFilters.sortBy ?? "color");
  const [view, setView] = useState(savedFilters.view ?? "all");
  // Narrow any view (and search) to in-laws only or descendants only; picking
  // one turns the other off.
  const [inLawsOnly, setInLawsOnly] = useState(savedFilters.inLawsOnly ?? false);
  const [descendantsOnly, setDescendantsOnly] = useState(savedFilters.descendantsOnly ?? false);

  useEffect(() => {
    try {
      sessionStorage.setItem(
        FILTERS_STORAGE_KEY,
        JSON.stringify({ searchQuery, sortBy, view, inLawsOnly, descendantsOnly })
      );
    } catch {
      /* the filters just start fresh next time */
    }
  }, [searchQuery, sortBy, view, inLawsOnly, descendantsOnly]);

  useEffect(() => {
    const onResize = () => setIsCompactLayout(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!allowed) return;
    const fetchReport = async () => {
      try {
        setLoading(true);
        setError("");
        const family_tree_id = getFamilyTreeIdFromStorage();
        if (!family_tree_id) {
          setMembers([]);
          return;
        }
        // The contact report has no head-of-line names; the dashboard statistics
        // supply them, the same way the Family Members page resolves them.
        const [response, statistics, hierarchy] = await Promise.all([
          getApi(`/report-management/member-contact-report?family_tree_id=${family_tree_id}`),
          getApi(`/dashboard-statistics-with-details?family_tree_id=${family_tree_id}`).catch(
            () => null
          ),
          getApi(`/family-tree-hierarchy-v2/${family_tree_id}`).catch(() => null),
        ]);
        if (hierarchy?.statusCode === 200 && hierarchy?.data) {
          setRecordContacts(collectRecordContacts(hierarchy.data.tree ?? hierarchy.data));
        }
        if (statistics?.statusCode === 200 && statistics?.data) {
          const allMembers = extractAllMembersFromStatisticsResponse(statistics.data);
          setTreeMembers(allMembers);
          const multipleBirths = statistics.data.multiple_births;
          setMultipleBirthRows(
            Array.isArray(multipleBirths?.details)
              ? multipleBirths.details
              : Array.isArray(multipleBirths)
                ? multipleBirths
                : []
          );
          setHeadsMap(
            buildHeadsOfLineIdByColorMap(
              extractHeadsOfLineFromStatisticsResponse(statistics.data),
              allMembers
            )
          );
        }
        if (response?.statusCode === 200) {
          setMembers(Array.isArray(response?.data?.members) ? response.data.members : []);
        } else {
          setMembers([]);
          setError(response?.message || "Could not load the report.");
        }
      } catch (err) {
        console.error("Error fetching member contact report:", err);
        setMembers([]);
        setError(err?.response?.data?.message || "Could not load the report.");
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, [allowed]);

  // The contact report carries no in-law, multiple-birth or birth-date fields;
  // the same member's statistics record does, so the name markers and ages match
  // Family Members. Members and their in-laws (from each member's spouse fields),
  // living only.
  const rows = useMemo(() => {
    const treeMemberById = new Map(
      treeMembers.map((tm) => [tm?.id ?? tm?.member_id, tm]).filter(([id]) => id)
    );
    const treeInLawByName = new Map(
      treeMembers.filter(isDashboardInLawMember).map((tm) => [nameKey(tm), tm])
    );
    const toRow = (m) => {
      const rawEmail = getEmail(m);
      const retired = isRetiredEmail(rawEmail);
      const age = getCurrentAge(m);
      return {
        ...m,
        // The contact report's is_spouse decides who is an in-law; the
        // statistics record merged in underneath only decides when the report
        // leaves it out, so a descendant is never hidden as an in-law.
        is_in_laws:
          m.is_spouse === undefined || m.is_spouse === null
            ? isDashboardInLawMember(m)
            : isSpouseRow(m),
        _name: getName(m),
        _descendantSpouseName: getDashboardMemberNameParts(m).descendantSpouseName,
        _phone: getCellPhone(m),
        _listPhone: getListPhone(m),
        _email: retired ? "" : rawEmail,
        _retiredEmail: retired ? rawEmail : "",
        _age: age,
        // Under a year old reads in months or days ("5 mos.", "12 days"), never a bare 0.
        _ageLabel: getMemberAgeLabel(m, age, { toToday: true }),
        _head: getDashboardFamilyLineHeadDisplayName(m, headsMap) || "Start/Top of Line",
      };
    };

    const recordIdOf = (contact) => {
      const id = toText(contact?.id);
      return isSpouseRow(contact) && id.endsWith(SPOUSE_ID_SUFFIX)
        ? id.slice(0, -SPOUSE_ID_SUFFIX.length)
        : id;
    };
    // The contact report skips anyone without a valid date of birth, so every
    // tree record it left out is added from the tree itself (with its spouse),
    // and the usual filters below decide whether they are listed.
    const reportedIds = new Set(members.map(recordIdOf));
    const missingFromReport = [...recordContacts.values()]
      .filter((record) => !reportedIds.has(toText(record.id)))
      .map(({ children, ...record }) => ({
        ...record,
        user_email: "",
        user_phone: "",
        user_country_code: "",
      }));
    const contacts = [...members, ...missingFromReport].map((contact) =>
      withRecordContact(contact, recordContacts.get(recordIdOf(contact)))
    );
    const memberRows = contacts.map((contact) => {
      // An in-law row's statistics record is found by name, as their id is the
      // member's with "_spouse" on the end.
      const treeRecord = isSpouseRow(contact)
        ? treeInLawByName.get(nameKey(contact))
        : treeMemberById.get(contact.id);
      return { ...toRow({ ...(treeRecord || {}), ...contact }), _recordId: recordIdOf(contact) };
    });
    // An in-law already listed as a member of their own is not repeated.
    const rowKey = (r) => `${nameKey(r)}|${r._email || r._retiredEmail}|${r._phone}`;
    const seen = new Set(memberRows.map(rowKey));
    const inLawRows = [];
    contacts.forEach((member) => {
      const spouse = getSpouseContact(member);
      if (!spouse) return;
      const treeInLaw = treeInLawByName.get(nameKey(spouse));
      // The in-law's own tree record carries their multiple-birth fields and id.
      const row = toRow(treeInLaw ? { ...treeInLaw, ...spouse, id: treeInLaw.id ?? spouse.id } : spouse);
      const key = rowKey(row);
      if (seen.has(key)) return;
      seen.add(key);
      // The in-law's details live on the spouse side of the member's record.
      inLawRows.push({ ...row, _recordId: recordIdOf(member) });
    });

    // The same person can reach the list from the report's spouse row, the
    // member's spouse fields and the tree records the report skipped, with
    // different contact details on each; they are listed once, with the copy
    // that has the most contact details. An in-law is one person per display
    // name (which carries their spouse's name), a descendant per record.
    const contactScore = (r) => (r._phone ? 1 : 0) + (r._email || r._retiredEmail ? 1 : 0);
    const personKey = (r) =>
      r.is_in_laws || isDashboardInLawMember(r)
        ? `in-law|${r._name.toLowerCase()}`
        : `member|${r._recordId || r.id || r._name.toLowerCase()}`;
    const byPerson = new Map();
    [...memberRows, ...inLawRows]
      .filter((r) => !isDeceased(r))
      .forEach((r) => {
        const key = personKey(r);
        const kept = byPerson.get(key);
        if (!kept || contactScore(r) > contactScore(kept)) byPerson.set(key, r);
      });
    return [...byPerson.values()];
  }, [members, treeMembers, headsMap, recordContacts]);


  // A twin whose own record carries no multiple-birth fields is still in the
  // set when a sibling's record links to them, so the tags are read across
  // every record the page has (contact report, statistics, its multiple_births
  // section and tree) rather than one row at a time.
  const multipleBirthSetSizes = useMemo(
    () =>
      buildMultipleBirthSetSizes([
        ...members,
        ...treeMembers,
        ...recordContacts.values(),
        ...multipleBirthRows,
      ]),
    [members, treeMembers, recordContacts, multipleBirthRows]
  );
  const getMultipleBirthTag = (m) =>
    getMultipleBirthSuperscriptText(m) ||
    getMultipleBirthTagForSize(multipleBirthSetSizes.get(toText(m?.id)));

  const legendLines = useMemo(
    () => buildDashboardColorLegendLines(treeMembers.length ? treeMembers : members, headsMap),
    [treeMembers, members, headsMap]
  );

  const sortedRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const byName = (a, b) => a._name.localeCompare(b._name, undefined, { sensitivity: "base" });
    // In-laws show no age, so they sort with the members whose age is unknown,
    // after everyone with one, by name.
    const ageOf = (m) => (isMarkedInLaw(m) || m._age === null ? null : m._age);
    return rows
      .filter((m) => {
        if (!isInView(m, view)) return false;
        if (inLawsOnly && !isMarkedInLaw(m)) return false;
        if (descendantsOnly && isMarkedInLaw(m)) return false;
        return (
          !q ||
          m._name.toLowerCase().includes(q) ||
          m._phone.toLowerCase().includes(q) ||
          m._listPhone.toLowerCase().includes(q) ||
          m._email.toLowerCase().includes(q) ||
          (view === "deleted-email" && m._retiredEmail.toLowerCase().includes(q)) ||
          toText(m.color_name).toLowerCase().includes(q) ||
          m._head.toLowerCase().includes(q)
        );
      })
      .sort((a, b) => {
        if (sortBy === "alpha-desc") return byName(b, a);
        if (sortBy in AGE_SORTS) {
          const ageA = ageOf(a);
          const ageB = ageOf(b);
          if (ageA === null || ageB === null) {
            return (ageA === null) - (ageB === null) || byName(a, b);
          }
          return (sortBy === "age-desc" ? ageB - ageA : ageA - ageB) || byName(a, b);
        }
        if (sortBy === "color") {
          return (
            toText(a.color_name).localeCompare(toText(b.color_name), undefined, {
              sensitivity: "base",
            }) || byName(a, b)
          );
        }
        return byName(a, b);
      });
  }, [rows, searchQuery, sortBy, view, inLawsOnly, descendantsOnly]);

  // One panel, filled top to bottom. A second panel starts only once the first
  // is full (with one empty row left at the bottom), and only when both fit side
  // by side at their natural width; otherwise the one panel scrolls, as on a
  // phone. Names, emails and lines are never cut off.
  const columnsRef = useRef(null);
  const [fitsTwoWide, setFitsTwoWide] = useState(false);
  const [rowsThatFit, setRowsThatFit] = useState(Infinity);
  const [layoutPass, setLayoutPass] = useState(0);

  useEffect(() => {
    const el = columnsRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => setLayoutPass((n) => n + 1));
    ro.observe(el);
    return () => ro.disconnect();
  }, [loading, members.length]);

  const split = fitsTwoWide && sortedRows.length > rowsThatFit;

  useLayoutEffect(() => {
    const el = columnsRef.current;
    if (!el) return;
    const style = window.getComputedStyle(el);
    const availableWidth =
      el.clientWidth - parseFloat(style.paddingLeft || 0) - parseFloat(style.paddingRight || 0);
    const panels = [...el.children];
    if (!panels.length) return;
    const panelStyle = window.getComputedStyle(panels[0]);
    const rowGap = parseFloat(panelStyle.rowGap) || 0;
    const contentTop =
      panels[0].getBoundingClientRect().top +
      (parseFloat(panelStyle.borderTopWidth) || 0) +
      (parseFloat(panelStyle.paddingTop) || 0);
    // The column labels sit above the rows in every panel, so they come off
    // the height the rows have.
    const head = panels[0].querySelector(":scope > .phone-report-head");
    const headHeight = head?.children.length
      ? Math.max(...[...head.children].map((cell) => cell.getBoundingClientRect().bottom)) -
        contentTop +
        rowGap
      : 0;
    // The list area carries the shared end-of-list blank row (App.css) as its
    // bottom padding, so that space stays free under a full first panel.
    const availableHeight =
      el.clientHeight -
      parseFloat(style.paddingTop || 0) -
      parseFloat(style.paddingBottom || 0) -
      headHeight;
    if (availableWidth <= 0 || availableHeight <= 0) return;

    // Height of one row plus the gap under it, read off the first panel,
    // measured from its top to the bottom of its last row.
    const firstRows = panels[0].querySelectorAll(":scope > .phone-report-row").length;
    const lastRow = panels[0].querySelector(":scope > .phone-report-row:last-child");
    const rowsBottom = lastRow
      ? Math.max(...[...lastRow.children].map((cell) => cell.getBoundingClientRect().bottom))
      : 0;
    const rowsHeight = lastRow?.children.length ? rowsBottom - contentTop - headHeight : 0;
    const rowPitch = firstRows ? (rowsHeight + rowGap) / firstRows : 0;
    if (rowPitch > 0) {
      const nextFit = Math.max(1, Math.floor((availableHeight + rowGap) / rowPitch));
      if (nextFit !== rowsThatFit) setRowsThatFit(nextFit);
    }

    // The space the divider takes between the two panels (margin + padding + border).
    const DIVIDER_SPACE = 48;
    const neededWidth =
      panels.length < 2
        ? // Each part is at most as wide as the whole list, so if two copies of
          // the single panel fit, the two parts do too.
          panels[0].offsetWidth * 2 + DIVIDER_SPACE
        : // The first panel's offsetWidth already holds its padding and border.
          panels.reduce((sum, panel) => sum + panel.offsetWidth, 0) +
          parseFloat(window.getComputedStyle(panels[0]).marginRight || 0);

    const nextFitsTwo = neededWidth <= availableWidth;
    if (nextFitsTwo !== fitsTwoWide) setFitsTwoWide(nextFitsTwo);
  }, [sortedRows, split, rowsThatFit, fitsTwoWide, layoutPass, loading, view, inLawsOnly, descendantsOnly]);

  if (!allowed) return <Navigate to="/dashboard" replace />;


  // The first panel takes everything that fits; the rest go in the second. When
  // even two full panels are not enough, the list is halved and both scroll.
  const perColumn = split
    ? Math.max(rowsThatFit, Math.ceil(sortedRows.length / 2))
    : sortedRows.length;
  const columns = [sortedRows.slice(0, perColumn), sortedRows.slice(perColumn)].filter(
    (c) => c.length
  );

  const openRecord = (m) =>
    navigate(buildMemberRecordEditPath(m._recordId, `${location.pathname}${location.search}`));

  const renderRow = (m, number) => {
    const canEdit = canEditRecords && !!m._recordId;
    const hex = toText(m.hex_code) || "#000000";
    const showInLawAsterisk = isMarkedInLaw(m);
    const multipleBirthSup = getMultipleBirthTag(m);
    // The in-law's "*" goes on the in-law's own name, before the
    // "(descendant)": "Lateasha Plant* (Maurice, Sr.)".
    const spouseSuffix = m._descendantSpouseName ? ` (${m._descendantSpouseName})` : "";
    const asteriskBeforeSpouse =
      showInLawAsterisk && Boolean(spouseSuffix) && m._name.endsWith(spouseSuffix);
    const nameLabel = asteriskBeforeSpouse ? (
      <>
        {m._name.slice(0, -spouseSuffix.length)}
        <DashboardInLawAsterisk className="total-family-members-page in-law-asterisk" />
        {spouseSuffix}
      </>
    ) : (
      m._name
    );
    return (
      <div key={m.id || number} className="phone-report-row">
        <span className="dashboard-family-line-number phone-report-number">{number}</span>
        <span className="phone-report-name" title={m._name}>
          {canEdit ? (
            <button
              type="button"
              className="phone-report-name-link"
              title={`Edit ${m._name}'s record`}
              onClick={() => openRecord(m)}
            >
              {nameLabel}
            </button>
          ) : (
            nameLabel
          )}
          {showInLawAsterisk && !asteriskBeforeSpouse && (
            <DashboardInLawAsterisk className="total-family-members-page in-law-asterisk" />
          )}
          {multipleBirthSup && <sup className="dashboard-multiple-birth-sup">{multipleBirthSup}</sup>}
        </span>
        {/* In-laws never show an age anywhere in the app; the empty cell keeps the columns aligned. */}
        <span className="phone-report-age">{showInLawAsterisk ? "" : m._ageLabel ?? "—"}</span>
        <span className="phone-report-phone">
          {/* For an admin the number (or the dash where it is missing) opens the
              record too, so a missing or wrong cell number can be fixed. */}
          {canEdit ? (
            <button
              type="button"
              className="phone-report-name-link phone-report-phone-link"
              title={`Edit ${m._name}'s record`}
              onClick={() => openRecord(m)}
            >
              {m._phone ? m._listPhone : "—"}
            </button>
          ) : m._phone ? (
            <a href={`tel:${m._phone.replace(/\s+/g, "")}`}>{m._listPhone}</a>
          ) : (
            "—"
          )}
        </span>
        {view === "deleted-email" ? (
          <span className="phone-report-email" title={m._retiredEmail || undefined}>
            {m._retiredEmail || "—"}
          </span>
        ) : (
          <span className="phone-report-email" title={m._email || undefined}>
            {m._email ? <a href={`mailto:${m._email}`}>{m._email}</a> : "—"}
          </span>
        )}
        <span className="phone-report-line">
          {m.color_name ? (
            <DashboardLineColorName colorName={m.color_name} hexCode={hex} headName={m._head} />
          ) : (
            <span style={{ color: hex }}>{m._head}</span>
          )}
        </span>
      </div>
    );
  };

  return (
    <div className="dashboard-container total-family-members-page dashboard-detail-page phone-number-report-page">
      <div className="dash-box dashboard-detail-box">
        <div className="dashboard-detail-header">
          <div className="dashboard-detail-header-left">
            <DashboardBackButton />
            <div className="dashboard-detail-heading-label-cluster">
              <h3 className="dashboard-detail-title">Contact List</h3>
              <div className="dashboard-detail-count">{sortedRows.length}</div>
            </div>
            {!loading && members.length > 0 && (
              <DashboardDetailSearchBox
                value={searchQuery}
                onChange={setSearchQuery}
                endAtDivider={2}
                centreOverDivider={false}
              />
            )}
            {!loading && members.length > 0 && (
              <div className="dashboard-detail-sort-toggle total-family-members-sort-toggle">
                {SORTS.map(({ value, label }) => (
                  <button
                    key={value}
                    type="button"
                    className={`dashboard-detail-sort-btn ${
                      sortBy === value ? "active" : ""
                    }`}
                    onClick={() => setSortBy(value)}
                  >
                    {label}
                  </button>
                ))}
                <button
                  type="button"
                  className={`dashboard-detail-sort-btn ${sortBy in AGE_SORTS ? "active" : ""}`}
                  onClick={() => setSortBy((prev) => (prev === "age-desc" ? "age-asc" : "age-desc"))}
                >
                  {AGE_SORTS[sortBy] || "Age"}
                </button>
                <span className="dashboard-detail-sort-divider" aria-hidden="true" />
                {VIEW_FILTERS.map(({ value, label }) => (
                  <button
                    key={value}
                    type="button"
                    className={`dashboard-detail-sort-btn ${view === value ? "active" : ""}`}
                    onClick={() => setView((prev) => (prev === value ? "all" : value))}
                  >
                    {label}
                  </button>
                ))}
                <span className="dashboard-detail-sort-divider" aria-hidden="true" />
                <button
                  type="button"
                  className={`dashboard-detail-sort-btn ${inLawsOnly ? "active" : ""}`}
                  onClick={() => {
                    setInLawsOnly((prev) => !prev);
                    setDescendantsOnly(false);
                  }}
                >
                  In-Laws
                </button>
                <button
                  type="button"
                  className={`dashboard-detail-sort-btn ${
                    descendantsOnly ? "active" : ""
                  }`}
                  onClick={() => {
                    setDescendantsOnly((prev) => !prev);
                    setInLawsOnly(false);
                  }}
                >
                  Descendants
                </button>
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <div className="dashboard-detail-empty-state">
            <p>Loading report...</p>
          </div>
        ) : error ? (
          <div className="dashboard-detail-empty-state">
            <p>{error}</p>
          </div>
        ) : members.length === 0 ? (
          <div className="dashboard-detail-empty-state">
            <p>No members found.</p>
          </div>
        ) : sortedRows.length === 0 ? (
          <div className="dashboard-detail-empty-state">
            {searchQuery.trim() ? (
              <DashboardDetailSearchNoMatch query={searchQuery} />
            ) : (
              <p>No members found.</p>
            )}
          </div>
        ) : (
          <div className="total-family-members-page dashboard-detail-content-wrapper">
            <div
              ref={columnsRef}
              className={`phone-report-columns${split ? "" : " phone-report-columns--single"}`}
            >
              {columns.map((list, colIndex) => (
                <div key={colIndex} className="phone-report-column">
                  <div className="phone-report-head" aria-hidden="true">
                    <span className="phone-report-head-number">#</span>
                    <span>Name</span>
                    <span className="phone-report-age">Age</span>
                    <span>Cell Phone</span>
                    <span>Email</span>
                    <span>Family Line</span>
                  </div>
                  {list.map((m, i) => renderRow(m, colIndex * perColumn + i + 1))}
                </div>
              ))}
            </div>
            <div className="total-family-members-page in-law-footer">
              <DashboardMarkerLegend
                multipleBirthTags={getMultipleBirthLegendTags(sortedRows, getMultipleBirthTag)}
                showDeceased={false}
              />
              {isCompactLayout && legendLines.length > 0 && (
                <DashboardColorLegend lines={legendLines} />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default PhoneNumberReport;
